// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AccessDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import { INSERT, UPDATE, DELETE } from 'logic/data/Izenda.ObjectStateConstants';
import { ASSIGN_TYPE } from 'logic/data/Izenda.AccessConstants';
import * as _ from 'lodash/fp';
import { EMPTY_GUID } from 'logic/data/Izenda.AppGlobalConstants';
import localizer from 'share/localization/Izenda.Localizer';

@tracker
export default class AccessDetail {
  @observable accessors: Array<any> = [];
  @observable accessRight = null;
  @observable assignedType = null;
  @observable id = null;
  @observable permissionId = null;
  @observable reportId = null;
  @observable selected = false;
  @observable state = INSERT;
  @observable accessRightId = null;
  @observable reportAccessRightId = null;
  @observable dashboardAccessRightId = null;
  @observable assignedTypeName = null;
  @observable accessorNames = [];
  @observable reportAccessRights = null;
  @observable dashboardAccessRights = null;
  tenantHierarchy = [];

  @computed
  get hasRolesAccessor() {
    return this.assignedType === ASSIGN_TYPE.ROLE;
  }

  @computed
  get hasUserAccessor() {
    return this.assignedType === ASSIGN_TYPE.USER;
  }

  @computed
  get hasTenantAccessor() {
    return this.assignedType === ASSIGN_TYPE.TENANT;
  }

  @computed
  get accessorsInfo() {
    return this.accessors.map(item => ({
      id: item,
      name: this.getAccessorName(item)
    }));
  }

  constructor(data, tenantHierarchy) {
    this.localRoles = [];
    this.tempId = _.uniqueId();
    this.tenantHierarchy = tenantHierarchy;
    data && this.populateData(data);
  }

  /**
   * Populate data
   * @param  {Object} access detail data
   */
  @action
  populateData(data = {}) {
    this.id = data.id;
    this.accessors = data.accessors || [];
    this.accessRight = data.accessRight;
    this.assignedType = data.assignedType;
    this.accessRightId = data.accessRightId;
    this.reportAccessRightId = data.reportAccessRightId;
    this.dashboardAccessRightId = data.dashboardAccessRightId;
    this.state = data.state;
    this.reportId = data.reportId;
    this.tempId = data.tempId || data.id;
    this.assignedTypeName = data.assignedTypeName;
    this.accessorNames = data.accessorNames || [];
    this.reportAccessRights = data.reportAccessRights;
    this.dashboardAccessRights = data.dashboardAccessRights;
  }

  /**
   * Update selected access
   * @param  {boolean} value
   */
  @action
  updateSelected(value) {
    this.selected = value;
  }

  /**
   * Check access  dirty or not
   * @return {boolean} result of dirty
   */
  checkDirty() {
    return this.isDirty;
  }

  /**
   * Update value by key
   * @param  {string} value
   * @param  {string} key
   */
  @action
  updateValueByKey(value, key) {
    this[key] = value;
    this.updateState();
    this.markDirty();
  }

  /**
   * Update access right
   * @param  {string} value
   * @param  {string} key
   */
  @action
  updateAccessRight(value, key) {
    this[key] = value.value;
    this.updateState();
    this.markDirty();
  }

  /**
   * delete action
   */
  @action
  delete() {
    this.selected = false;
    this.state = DELETE;
    this.markDirty();
  }

  /**
   * Update assigned type
   * @param  {string} value
   */
  @action
  updateAssignedType(value) {
    this.assignedType = value;
    this.accessors = [];
    this.accessorNames = [];
    this.updateState();
    this.markDirty();
  }

  /**
   * Add more accessors
   * @param  {string} value
   */
  @action
  addMoreAccessors(value) {
    // remove all tenants if we're adding tenant group which containing them
    if (this.hasTenantAccessor) {
      const oldAccessors = this.accessors;
      const newAccessors = _.map('id', value);
      _.flow(
        _.filter(newAccessor => this.isAccessorTenantGroup(newAccessor)),
        _.map(newAccessor => {
          const tenantGroup = _.find({tenantGroupId: newAccessor}, this.tenantHierarchy);
          if (tenantGroup) {
            // if new item is tenant group: find and remove nested tenant accessors
            const tenantGroupAccessorIds = _.map('tenantId', tenantGroup.tenantGroupItems);
            const accessorsToRemove = _.filter(oldAccessor => _.some(x => x === oldAccessor, tenantGroupAccessorIds), oldAccessors);
            _.forEach(x => this.removeAccessor(x), accessorsToRemove);
          }
        })
      )(newAccessors);
    }

    // add
    _.forEach(item => {
      if (!_.isEmpty(item) && !_.includes(item.id, this.accessors)) {
        this.accessors.push(item.id);
        this.accessorNames.push(item.name || item.fullName);
      }
    }, value);
    this.updateState();
    this.markDirty();
  }

  /**
   * Remove accessor
   */
  removeAccessor(accessor) {
    const index = this.accessors.indexOf(accessor);
    if (index < 0)
      return;
    this.accessors.splice(index, 1);
    this.accessorNames.splice(index, 1);
  }

  /**
   * Build access data for save in tenant/ role settings
   * @return {object}
   */
  buildDataForSaveAccessSettings() {
    return {
      state: this.state,
      assignedType: this.assignedType,
      accessors: this.accessors,
      tempId: this.tempId,
      id: this.id,
      reportAccessRightId: this.reportAccessRightId,
      dashboardAccessRightId: this.dashboardAccessRightId
    };
  }

  /**
   * Update state of access detail if current state # Insert
   */
  @action
  updateState() {
    if (this.state !== INSERT) {
      this.state = UPDATE;
    }
  }

  /**
   * Update accessor list
   * @param  {Array} accessors
   */
  @action
  updateAccessorList(accessors) {
    this.accessors.splice(0, this.accessors.length);
    this.accessorNames.splice(0, this.accessorNames.length);
    this.localRoles.splice(0, this.localRoles.length);

    _.forEach(item => {
      this.accessors.push(item.id);
      this.accessorNames.push(item.name || item.fullName);
      _.isNil(item.tenantId) && this.localRoles.push(item.id);
    }, accessors);
    this.updateState();
    this.markDirty();
  }

  /**
   * Remove all global roles
   */
  @action
  removeAllGlobalRoles() {
    this.accessors = this.localRoles;
  }

  /**
   * Check whether accessor is tenant group or not
   * @param accessorId
   * @returns {boolean}
   */
  isAccessorTenantGroup(accessorId) {
    if (!this.hasTenantAccessor)
      return false;
    const tenantGroupIds = _.flow(_.map('tenantGroupId'), _.filter(x => x !== EMPTY_GUID))(this.tenantHierarchy || []);
    return _.indexOf(accessorId, tenantGroupIds) >= 0;
  }

  /**
   * Get accessor name for UI
   * @param accessorId
   * @returns {string}
   */
  getAccessorName(accessorId) {
    const index = _.indexOf(accessorId, this.accessors);
    let accessorName = this.accessorNames[index];
    if (!this.hasTenantAccessor)
      return accessorName;
    const prefix = this.isAccessorTenantGroup(accessorId)
      ? localizer.getText('COMMON_LABEL_TENANT_GROUP')
      : localizer.getText('COMMON_LABEL_TENANT');
    if (accessorName.startsWith(prefix + ': '))
      return accessorName;
    return `${prefix}: ${accessorName}`;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.AccessDetail.js